import { useId, useMemo, useState, type ReactNode } from 'react';
import type { DataTableMode } from '@odo/shared-ui';
import { useSessionTableMode } from '../../../../hooks/useSessionTableMode';
import { BrowserSearchBox } from './BrowserSearchBox';
import { BrowserSegments } from './BrowserSegments';
import { BrowserToolbar } from './BrowserToolbar';
import { panelId, tabId, type IteroBrowserTab } from './browserTabs';
import { OrderFilterBar } from './filter-bar/OrderFilterBar';
import { PatientFilterBar } from './filter-bar/PatientFilterBar';
import { IteroBrowserFrame } from './IteroBrowserFrame';
import { IteroBrowserScopeContext } from './iteroBrowserScope';
import { localIsoDay } from './isoDay';
import { OrdersPanel } from './OrdersPanel';
import { PatientsPanel } from './PatientsPanel';
import { ORDER_SEARCH_PLACEHOLDER, PATIENT_SEARCH_PLACEHOLDER } from './searchInput';
import { useIteroBrowserNavigation } from './useIteroBrowserNavigation';
import { useLatchedFlag } from './useLatchedFlag';
import { useOrdersBrowse, type OrdersBrowse } from './useOrdersBrowse';
import { usePatientsBrowse, type PatientsBrowse } from './usePatientsBrowse';

// A UI preference only (never patient data), shared by both tabs.
const VIEW_MODE_STORAGE_KEY = 'odo:itero-browser:table-mode';

interface TabPanelProps {
    baseId: string;
    tab: IteroBrowserTab;
    isActive: boolean;
    children: ReactNode;
}

// Content mounts on first visit and then stays, so switching tabs keeps filters, page and selection.
function TabPanel({ baseId, tab, isActive, children }: TabPanelProps) {
    return (
        <div
            id={panelId(baseId, tab)}
            role="tabpanel"
            aria-labelledby={tabId(baseId, tab)}
            className={isActive ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}
        >
            {children}
        </div>
    );
}

interface BrowserToolbarRowProps {
    baseId: string;
    activeTab: IteroBrowserTab;
    patients: PatientsBrowse;
    orders: OrdersBrowse;
    today: string;
}

function BrowserToolbarRow({ baseId, activeTab, patients, orders, today }: BrowserToolbarRowProps) {
    const isPatients = activeTab === 'patients';
    // With patient search switched off upstream there is no list to filter.
    const showListControls = !isPatients || !patients.isDisabled;
    const search = (
        <BrowserSearchBox
            label={isPatients ? 'Search iTero patients' : 'Search iTero orders by chart number'}
            placeholder={isPatients ? PATIENT_SEARCH_PLACEHOLDER : ORDER_SEARCH_PLACEHOLDER}
            controls={panelId(baseId, activeTab)}
            value={isPatients ? patients.filters.search : orders.filters.search}
            disabled={isPatients && patients.isDisabled}
            onChange={(value) =>
                isPatients
                    ? patients.updateFilters({ search: value })
                    : orders.updateFilters({ search: value })
            }
        />
    );

    return (
        <BrowserToolbar search={search}>
            {showListControls &&
                (isPatients ? (
                    <PatientFilterBar browse={patients} today={today} />
                ) : (
                    <OrderFilterBar browse={orders} today={today} />
                ))}
        </BrowserToolbar>
    );
}

// Tab and selections live in the URL (useIteroBrowserNavigation); filters, page and mode stay here.
function useIteroBrowserState() {
    const nav = useIteroBrowserNavigation();
    const ordersVisited = useLatchedFlag(nav.activeTab === 'orders');
    const [today] = useState(() => localIsoDay(new Date()));
    const [viewMode, setViewMode] = useSessionTableMode(VIEW_MODE_STORAGE_KEY);
    const patients = usePatientsBrowse(today, viewMode);
    const orders = useOrdersBrowse(today, ordersVisited, viewMode);

    const changeViewMode = (next: DataTableMode) => {
        if (next === viewMode) return;
        patients.resetScroll();
        orders.resetScroll();
        setViewMode(next);
    };

    return { nav, ordersVisited, today, patients, orders, changeViewMode };
}

interface IteroBrowserProps {
    practiceName: string;
    clinicId: number;
    onClose: () => void;
}

// Both tabs' state lives here: the toolbar, the Patients count and the shared view mode span both panels.
function IteroBrowser({ practiceName, clinicId, onClose }: IteroBrowserProps) {
    const baseId = useId();
    const { nav, ordersVisited, today, patients, orders, changeViewMode } = useIteroBrowserState();
    const { activeTab, selectTab } = nav;

    return (
        <IteroBrowserFrame
            titleId={`${baseId}-title`}
            title={practiceName}
            subtitle={`iTero clinic ${clinicId}`}
            onClose={onClose}
            headerControls={
                <BrowserSegments
                    baseId={baseId}
                    activeTab={activeTab}
                    counts={{ patients: patients.total }}
                    onChange={selectTab}
                />
            }
            toolbar={
                <BrowserToolbarRow
                    baseId={baseId}
                    activeTab={activeTab}
                    patients={patients}
                    orders={orders}
                    today={today}
                />
            }
        >
            <TabPanel baseId={baseId} tab="patients" isActive={activeTab === 'patients'}>
                <PatientsPanel
                    browse={patients}
                    nav={nav.patients}
                    today={today}
                    onModeChange={changeViewMode}
                    onGoToOrders={() => selectTab('orders')}
                />
            </TabPanel>
            <TabPanel baseId={baseId} tab="orders" isActive={activeTab === 'orders'}>
                {ordersVisited && (
                    <OrdersPanel
                        browse={orders}
                        nav={nav.orders}
                        today={today}
                        onModeChange={changeViewMode}
                    />
                )}
            </TabPanel>
        </IteroBrowserFrame>
    );
}

interface IteroBrowserModalProps {
    practiceId: string;
    practiceName: string;
    clinicId: number;
    onClose: () => void;
}

export function IteroBrowserModal({
    practiceId,
    practiceName,
    clinicId,
    onClose
}: IteroBrowserModalProps) {
    const scope = useMemo(() => ({ practiceId, clinicId }), [practiceId, clinicId]);
    return (
        <IteroBrowserScopeContext.Provider value={scope}>
            <IteroBrowser practiceName={practiceName} clinicId={clinicId} onClose={onClose} />
        </IteroBrowserScopeContext.Provider>
    );
}
